import {
  clearFloors,
  selectSingleWorkplace,
  selectTeamMemberWorkplace,
  selectVehicleBooking,
  setInputs
} from "../slices/booking.slice";
import { BookingType, NotSerializedBookingInputs } from "../typings/booking-inputs";
import { visitorRequestsPlaceOrRoom } from "./visitor-registration.functions";
import { AppDispatch } from "../../../app/store";
import { PricingModel, UserInformation } from "../../Login/typings/login.types";

export const nextStepCalc = (step: number, to?: number) => {
  if (to && (to < 1 || to > 6)) throw new Error("Unknown Step");
  return to || step + 1;
};

export const prevStepCalc = (
  step: number,
  inputs: NotSerializedBookingInputs,
  dispatch: AppDispatch,
  to?: number
) => {
  if (to && (to < 1 || to > 6)) throw new Error("Unknown Step");

  const s = to || step - 1;

  if (
    inputs.mode === BookingType.VISITOR &&
    (step === 3 || step === 4) &&
    !visitorRequestsPlaceOrRoom(inputs) &&
    to === undefined
  ) {
    dispatch(clearFloors());
    dispatch(setInputs({ floorInventoryId: undefined }));
    return 2;
  }

  // when automated seating was selected, go back to selecting location step not the selecting floor step
  if (inputs.automatedSeating || inputs.isMobileWorking) {
    dispatch(setInputs({ automatedSeating: false, isMobileWorking: false }));
    return 2;
  }

  if (s === 2 || (inputs.mode === BookingType.TEAM && s === 3)) {
    dispatch(clearFloors());
    dispatch(setInputs({ selectedLocation: undefined, floorInventoryId: undefined }));
  }
  return s;
};

/**
 * when booking mode is either WORKPLACE, PARKINGPLACE, ELECTRICCHARGINGSTATIONPLACE, or VEHICLE
 */
export const placeMode = (inputs: NotSerializedBookingInputs) =>
  inputs.mode === BookingType.WORKPLACE ||
  inputs.mode === BookingType.PARKINGPLACE ||
  inputs.mode === BookingType.ELECTRICCHARGINGSTATIONPLACE ||
  inputs.mode === BookingType.VEHICLE ||
  inputs.mode === BookingType.VISITOR;

/**
 * when booking mode is either WORKPLACE, PARKINGPLACE, or ELECTRICCHARGINGSTATIONPLACE
 */
export const placeModeExcludeVehicle = (inputs: NotSerializedBookingInputs) =>
  inputs.mode === BookingType.WORKPLACE ||
  inputs.mode === BookingType.PARKINGPLACE ||
  inputs.mode === BookingType.ELECTRICCHARGINGSTATIONPLACE;

/**
 * when booking mode is VEHICLE
 */
export const checkModeVehicle = (inputs: NotSerializedBookingInputs) =>
  inputs.mode === BookingType.VEHICLE;

export const checkModeVisitor = (inputs: NotSerializedBookingInputs) =>
  inputs.mode === BookingType.VISITOR;

/**
 * when booking mode is either CONFERENCEZONE or PLACEZONE
 */
export const zoneMode = (inputs: NotSerializedBookingInputs) =>
  inputs.mode === BookingType.CONFERENCEZONE || inputs.mode === BookingType.PLACEZONE;

/**
 * Checks if the current step and mode combination should trigger a reset
 */
const shouldResetSelection = (step: string, inputs: NotSerializedBookingInputs): boolean => {
  return (
    (placeMode(inputs) && step === "3") ||
    (inputs.mode === BookingType.TEAM && step === "4") ||
    (inputs.mode === BookingType.CONFERENCEZONE && step === "4")
  );
};

/**
 * Checks if any user has a selected booking inventory
 */
const hasSelectedBooking = (
  usersBookedFor: NotSerializedBookingInputs["usersBookedFor"]
): boolean => {
  return usersBookedFor?.some(user => Boolean(user.bookingInventoryId)) ?? false;
};

/**
 * Resets team member workplaces
 */
export const resetTeamWorkplaces = (
  usersBookedFor: NotSerializedBookingInputs["usersBookedFor"],
  dispatch: AppDispatch
): void => {
  if (!usersBookedFor) return;

  usersBookedFor.forEach((_, index) => {
    dispatch(selectTeamMemberWorkplace({ index, placeInventoryId: undefined }));
  });
};

/**
 * Resets single workplace selection
 */
const resetSingleWorkplace = (userInformation: UserInformation, dispatch: AppDispatch): void => {
  dispatch(
    selectSingleWorkplace({
      firstName: userInformation.firstName,
      surname: userInformation.surname,
      userId: userInformation.sub,
      bookingInventoryId: undefined,
      email: userInformation.email,
      isExternal: false
    })
  );
};

/**
 * Resets vehicle
 */
const resetVehicleBooking = (userInformation: UserInformation, dispatch: AppDispatch): void => {
  dispatch(
    selectVehicleBooking({
      usersBookedFor: {
        firstName: userInformation.firstName,
        surname: userInformation.surname,
        userId: userInformation.sub,
        bookingInventoryId: undefined,
        email: userInformation.email,
        isExternal: false
      },
      vehicleInfo: undefined
    })
  );
};

/**
 * Resets conference service if needed
 */
const resetConferenceService = (
  mode: NotSerializedBookingInputs["mode"],
  dispatch: AppDispatch
): void => {
  if (mode === BookingType.CONFERENCEZONE) {
    dispatch(setInputs({ conferenceService: undefined }));
  }
};

export const resetSelection = (
  step: string,
  inputs: NotSerializedBookingInputs,
  userInformation: UserInformation,
  dispatch: AppDispatch
) => {
  // Check if we should reset based on step and mode
  if (!shouldResetSelection(step, inputs)) return;

  // Check if any booking is selected
  if (!hasSelectedBooking(inputs.usersBookedFor)) return;

  // Reset based on booking mode
  if (inputs.mode === BookingType.TEAM) {
    resetTeamWorkplaces(inputs.usersBookedFor, dispatch);
  } else {
    resetSingleWorkplace(userInformation, dispatch);
    resetVehicleBooking(userInformation, dispatch);
    resetConferenceService(inputs.mode, dispatch);
  }
};

export const disableNextRoomBookingInputs = (
  inputs: NotSerializedBookingInputs,
  userInformation: UserInformation
) => {
  if (
    inputs.mode === BookingType.PLACEZONE &&
    userInformation.pricingModels.includes(PricingModel.ENTERPRISE)
  ) {
    return !inputs.restrictionObject?.object?.zoneBookingObject;
  } else
    return (
      !inputs.zoneAccess ||
      inputs.usersBookedFor!.some(({ bookingInventoryId }) => !bookingInventoryId)
    );
};
